import React, { useState } from "react";
import {
  DocumentArrowDownIcon,
  DocumentTextIcon,
  TableCellsIcon,
  CodeBracketIcon,
} from "@heroicons/react/24/outline";
import api from "../utils/api";

const INRExportFormatsButton = ({ filters }) => {
  const [isExporting, setIsExporting] = useState({});
  const [exportStatus, setExportStatus] = useState({});

  const exportData = async (format) => {
    const formatKey = format.toLowerCase();
    setIsExporting((prev) => ({ ...prev, [formatKey]: true }));
    setExportStatus((prev) => ({ ...prev, [formatKey]: "exporting" }));

    try {
      // Build query parameters from filters
      const params = Object.entries(filters)
        .filter(([_, v]) => v)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("&");

      const response = await api.get(
        `/api/export-formats/inr-investors/${formatKey}?${params}`,
        {
          responseType: "blob",
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Set filename based on format
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `inr_investors_export_${timestamp}.${formatKey}`;
      link.setAttribute("download", filename);

      // Trigger download
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setExportStatus((prev) => ({ ...prev, [formatKey]: "success" }));
      setTimeout(() => {
        setExportStatus((prev) => ({ ...prev, [formatKey]: null }));
      }, 3000);
    } catch (error) {
      console.error(`Error exporting ${format}:`, error);
      // Show permission alert for export action
      if (error.response?.status === 403) {
        alert("You do not have permission to export INR investor data.");
      }
      setExportStatus((prev) => ({ ...prev, [formatKey]: "error" }));
      setTimeout(() => {
        setExportStatus((prev) => ({ ...prev, [formatKey]: null }));
      }, 3000);
    } finally {
      setIsExporting((prev) => ({ ...prev, [formatKey]: false }));
    }
  };

  const exportFormats = [
    {
      name: "CSV",
      icon: DocumentTextIcon,
      description: "Comma-separated values",
      color: "bg-green-50 text-green-700 border-green-200 hover:bg-green-100",
    },
    {
      name: "XLSX",
      icon: TableCellsIcon,
      description: "Excel spreadsheet",
      color: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100",
    },
    {
      name: "JSON",
      icon: CodeBracketIcon,
      description: "JavaScript Object Notation",
      color:
        "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100",
    },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case "exporting":
        return (
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
        );
      case "success":
        return (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "error":
        return (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative">
      {/* Main Export Button - Dropdown Toggle */}
      <div className="group">
        <button
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          onClick={(e) => {
            const dropdown = e.currentTarget.nextElementSibling;
            dropdown.classList.toggle("hidden");
          }}
        >
          <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
          Export INR Data
          <svg
            className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:rotate-180"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Dropdown Menu */}
        <div className="hidden absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          <div className="p-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <DocumentArrowDownIcon className="h-4 w-4 mr-2 text-gray-500" />
              Export INR Investor Data
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Choose your preferred format to download filtered results
            </p>
          </div>

          <div className="p-2 space-y-1">
            {exportFormats.map((format) => {
              const formatKey = format.name.toLowerCase();
              const isLoading = isExporting[formatKey];
              const status = exportStatus[formatKey];
              const IconComponent = format.icon;

              return (
                <button
                  key={format.name}
                  onClick={() => exportData(format.name)}
                  disabled={isLoading}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${format.color}`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium">{format.name}</div>
                      <div className="text-xs opacity-75">
                        {format.description}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {status && (
                      <div
                        className={`flex items-center space-x-1 text-xs px-2 py-1 rounded-full ${
                          status === "success"
                            ? "bg-green-100 text-green-700"
                            : status === "error"
                            ? "bg-red-100 text-red-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {getStatusIcon(status)}
                        <span>
                          {status === "exporting"
                            ? "Exporting..."
                            : status === "success"
                            ? "Downloaded"
                            : "Failed"}
                        </span>
                      </div>
                    )}

                    {!status && (
                      <svg
                        className="w-4 h-4 opacity-50"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="p-3 border-t border-gray-100 bg-gray-50 rounded-b-lg">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>💡 Tip: Files include current filter results</span>
              <span className="flex items-center">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
                Live Data
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close dropdown */}
      <div
        className="fixed inset-0"
        onClick={(e) => {
          const dropdown = document.querySelector(".group .hidden");
          if (dropdown && !dropdown.classList.contains("hidden")) {
            dropdown.classList.add("hidden");
          }
        }}
        style={{ display: "none", zIndex: 40 }}
      ></div>
    </div>
  );
};

export default INRExportFormatsButton;
